import { ICommand } from './ICommand';
import { useDiagramStore } from '../../stores/diagramStore';
import VersionedComponentRegistry from '../../lib/diagram/VersionedComponentRegistry';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { getShapeManifest } from '../../lib/diagram/registries';
import { validateShapeLimitOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import i18n from '../../i18n/config';
import type * as Y from 'yjs';
import { getTableDropHitAtPoint } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { resolveCellLayoutConfig } from '../../domains/diagram/shapes/table/tableCellLayout.registry';
import { getCommandManager } from '../../hooks/useCommandManager';
import { DockNodeToTableCellCommand } from './DockNodeToTableCellCommand';
import { ReflowDockedNodesInTableCellCommand } from './ReflowDockedNodesInTableCellCommand';
import { AutoResizeTableCellToFitDockedContentCommand } from './AutoResizeTableCellToFitDockedContentCommand';
import { Tracker, TABLE, TABLE_ADVANCED } from '../../domains/analytics';
import { snapPointToGrid } from '../../lib/diagram/gridSnap';
import {
  collectMediaKeysFromNodes,
  emitDiagramMediaReferenceDelta,
} from '../../domains/media/utils/commandMediaReferenceEvents';
import {
  getOrgChartDefaultDepartmentName,
  getOrgChartDefaultPersonName,
  getOrgChartDefaultPositionTitle,
  getOrgChartDefaultRole,
} from '../../domains/orgchart/models/defaults';
import { resolveRegisteredShapeCategoryManifest } from '../../lib/diagram/shapeCategoryManifestResolver';

export interface CreateShapeParams {
  id?: string;
  type: string;
  x: number;
  y: number;
  snapToGrid?: boolean;
  width?: number;
  height?: number;
  text?: string;
  fillColor?: string;
  strokeColor?: string;
  fontSize?: number;
  fontFamily?: string;
  category?: string;
  attrs?: Record<string, unknown>;
  /** Visual defaults applied before first persistence; semantic fields are ignored. */
  initialStyle?: Readonly<Record<string, unknown>>;
  ydoc?: Y.Doc; // DEPRECATED: Use MutationManager instead
}

export class CreateShapeCommand implements ICommand {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  
  private params: CreateShapeParams;
  private createdId?: string;
  private executed = false;
  // Persist the exact node created so redo can faithfully restore it
  private createdNode?: any;

  constructor(params: CreateShapeParams) {
    this.id = `create-shape-${Date.now()}`;
    this.name = 'Create Shape';
    this.description = `Create ${params.type} shape`;
    this.timestamp = Date.now();
    this.params = params;
    // Note: We no longer store ydoc, we use MutationManager which always has the latest doc
  }

  execute(): void {
    if (this.executed) {
      return;
    }
    
    // Check write permissions before creating shape
    const store = useDiagramStore.getState();
    if (!store.canWrite) {
      try { console.debug('Debug SDI: CreateShapeCommand.execute blocked (no write permission)'); } catch {}
      return;
    }
    
    // Check shape limits
    if (!validateShapeLimitOrPromptUpgrade(store)) {
      return; 
    }
    
    // Generate unique ID (or use caller-provided deterministic id)
    this.createdId = this.params.id ?? `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const originalPos = { x: this.params.x, y: this.params.y };
    const initialPos = (this.params.snapToGrid ?? true) ? snapPointToGrid(originalPos) : originalPos;
    
    // Map shape types to proper node configuration
    const newShape: any = {
      id: this.createdId,
      x: initialPos.x,
      y: initialPos.y,
    };
    
    // Handle genogram-specific shapes (including gender category which maps to genogram)
    if (this.params.category === 'genogram' || this.params.category === 'gender') {
      newShape.type = 'person';
      // Get default dimensions from person manifest
      const personManifest = getShapeManifest('genogram', 'person');
      const defaultDims = personManifest?.defaultDimensions || { width: 100, height: 100 };
      const width = this.params.width || defaultDims.width;
      const height = this.params.height || defaultDims.height;
      // Set both width/height AND w/h for consistency with Electric projection
      newShape.width = width;
      newShape.height = height;
      newShape.w = width;
      newShape.h = height;
      const v = VersionedComponentRegistry.getDefaultVersion('person', 'shape');
      (newShape as any).version = v;
      
      switch (this.params.type) {
        case 'male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.male', { defaultValue: 'Male' }) as string;
          break;
        case 'female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.female', { defaultValue: 'Female' }) as string;
          break;
        case 'non-binary':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.nonBinary', { defaultValue: 'Non-binary' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), genderIdentity: 'non-binary' };
          break;
        case 'unknown':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.unknown', { defaultValue: 'Unknown' }) as string;
          break;
        case 'pregnancy':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.pregnancy', { defaultValue: 'Pregnancy' }) as string;
          newShape.attrs = { pregnancy: true };
          break;
        case 'deceased-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.deceased', { defaultValue: 'Deceased' }) as string;
          newShape.deceased = true;
          break;
        case 'deceased-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.deceased', { defaultValue: 'Deceased' }) as string;
          newShape.deceased = true;
          break;
        case 'deceased-unknown':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.deceased', { defaultValue: 'Deceased' }) as string;
          newShape.deceased = true;
          break;
        case 'transgender-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.transgenderMale', { defaultValue: 'Transgender Male' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), genderIdentity: 'transgender' };
          break;
        case 'transgender-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.transgenderFemale', { defaultValue: 'Transgender Female' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), genderIdentity: 'transgender' };
          break;
        case 'index-person':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.indexPerson', { defaultValue: 'Index Person' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), isIndexPerson: true };
          break;
        case 'pregnant':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.pregnant', { defaultValue: 'Pregnancy' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), pregnancy: true };
          break;
        case 'gay':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.gay', { defaultValue: 'Gay' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), sexualOrientation: 'gay' };
          break;
        case 'lesbian':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.lesbian', { defaultValue: 'Lesbian' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), sexualOrientation: 'lesbian' };
          break;
        case 'bisexual-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.bisexualMale', { defaultValue: 'Bisexual Male' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), sexualOrientation: 'bisexual' };
          break;
        case 'bisexual-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.bisexualFemale', { defaultValue: 'Bisexual Female' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), sexualOrientation: 'bisexual' };
          break;
        case 'agender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.agender', { defaultValue: 'Agender' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'agender' };
          break;
        case 'agender-classic':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.agender', { defaultValue: 'Agender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'agender-classic' };
          break;
        case 'two-spirit':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.twoSpirit', { defaultValue: 'Two-Spirit' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'two-spirit' };
          break;
        case 'gender-fluid':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.genderFluid', { defaultValue: 'Gender Fluid' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'gender-fluid' };
          break;
        case 'gender-fluid-classic':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.genderFluid', { defaultValue: 'Gender Fluid' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'gender-fluid-classic' };
          break;
        case 'transgender-classic':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.transgender', { defaultValue: 'Transgender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'transgender-classic' };
          break;
        case 'transgender-alt':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.transgender', { defaultValue: 'Transgender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'transgender-alt' };
          break;
        case 'genderqueer':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.genderqueer', { defaultValue: 'Genderqueer' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'genderqueer' };
          break;
        case 'bigender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.bigender', { defaultValue: 'Bigender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'bigender' };
          break;
        case 'trigender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.trigender', { defaultValue: 'Trigender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'trigender' };
          break;
        case 'trigender-equal':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.trigender', { defaultValue: 'Trigender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'trigender-equal' };
          break;
        case 'classic-male':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.male', { defaultValue: 'Male' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'classic-male' };
          break;
        case 'classic-female':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.female', { defaultValue: 'Female' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'classic-female' };
          break;
        case 'pangender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.pangender', { defaultValue: 'Pangender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'pangender' };
          break;
        case 'xenogender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.xenogender', { defaultValue: 'Xenogender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'xenogender' };
          break;
        case 'demigender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.demigender', { defaultValue: 'Demigender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'demigender' };
          break;
        case 'androgyne':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.androgyne', { defaultValue: 'Androgyne' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'androgyne' };
          break;
        case 'demiboy':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.demiboy', { defaultValue: 'Demiboy' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'demiboy' };
          break;
        case 'demigirl':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.demigirl', { defaultValue: 'Demigirl' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'demigirl' };
          break;
        case 'intergender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.intergender', { defaultValue: 'Intergender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'intergender' };
          break;
        case 'travesti':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.travesti', { defaultValue: 'Travesti' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'travesti' };
          break;
        case 'third-gender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.thirdGender', { defaultValue: 'Third Gender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'third-gender' };
          break;
        case 'hijra':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.hijra', { defaultValue: 'Hijra' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'hijra' };
          break;
        case 'neurogender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.neurogender', { defaultValue: 'Neurogender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'neurogender' };
          break;
        case 'detragender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.detragender', { defaultValue: 'Detragender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'detragender' };
          break;
        case 'centrgender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.centrgender', { defaultValue: 'Centrgender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'centrgender' };
          break;
        case 'isogender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.isogender', { defaultValue: 'Isogender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'isogender' };
          break;
        case 'ultergender':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.ultergender', { defaultValue: 'Ultergender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'ultergender' };
          break;
        case 'ultergender-equal':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.ultergender', { defaultValue: 'Ultergender' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'ultergender-equal' };
          break;
        case 'questioning':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.questioning', { defaultValue: 'Questioning' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'questioning' };
          break;
        case 'queer':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.queer', { defaultValue: 'Queer' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'queer' };
          break;
        case 'polyamorous':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.polyamorous', { defaultValue: 'Polyamorous' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'polyamorous' };
          break;
        case 'self-partnering':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.selfPartnering', { defaultValue: 'Self-Partnering' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'self-partnering' };
          break;
        case 'nonamorous':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.nonamorous', { defaultValue: 'Nonamorous' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'nonamorous' };
          break;
        case 'ambiamorous':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.ambiamorous', { defaultValue: 'Ambiamorous' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'ambiamorous' };
          break;
        case 'polyflexible':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.polyflexible', { defaultValue: 'Polyflexible' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'polyflexible' };
          break;
        case 'monoflexible':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.monoflexible', { defaultValue: 'Monoflexible' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'monoflexible' };
          break;
        case 'male-equal':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.male', { defaultValue: 'Male' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'male-equal' };
          break;
        case 'female-equal':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.female', { defaultValue: 'Female' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'female-equal' };
          break;
        case 'intersex':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.intersex', { defaultValue: 'Intersex' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'intersex' };
          break;
        case 'epicene':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.epicene', { defaultValue: 'Epicene' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'epicene' };
          break;
        case 'epicene-classic':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.epicene', { defaultValue: 'Epicene' }) as string;
          newShape.attrs = { ...(newShape.attrs || {}), shapeType: 'epicene-classic' };
          break;
        case 'stillbirth-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.stillbirth', { defaultValue: 'Stillbirth' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), stillbirth: true };
          break;
        case 'stillbirth-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.stillbirth', { defaultValue: 'Stillbirth' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), stillbirth: true };
          break;
        case 'institution':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.institution', { defaultValue: 'Institution' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'institution' };
          break;
        case 'pet':
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.pet', { defaultValue: 'Pet' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'pet' };
          break;
        case 'culture-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.cultureMale', { defaultValue: 'More than 2 Culture Male' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'culture-male' };
          break;
        case 'culture-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.cultureFemale', { defaultValue: 'More than 2 Culture Female' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'culture-female' };
          break;
        case 'immigration-male':
          newShape.sex = 'male';
          newShape.name = i18n.t('genogram:shapes.immigrationMale', { defaultValue: 'Immigration - Male' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'immigration-male' };
          break;
        case 'immigration-female':
          newShape.sex = 'female';
          newShape.name = i18n.t('genogram:shapes.immigrationFemale', { defaultValue: 'Immigration - Female' }) as string;
          newShape.attrs = { ...(newShape.attrs||{}), shapeType: 'immigration-female' };
          break;
        default:
          // Default to unknown person
          newShape.sex = 'unknown';
          newShape.name = i18n.t('genogram:shapes.newPerson', { defaultValue: 'New Person' }) as string;
      }
    } else if (this.params.category === 'text' || this.params.type === 'text') {
      // Handle text as a special case - create a text-only node
      newShape.type = 'text';
      newShape.text = this.params.text || 'Text';
      // Get default dimensions from text manifest
      const textManifest = getShapeManifest('genogram', 'text');
      const defaultDims = textManifest?.defaultDimensions || { width: 100, height: 30 };
      const width = this.params.width || defaultDims.width;
      const height = this.params.height || defaultDims.height;
      // Set both width/height AND w/h for consistency with Electric projection
      newShape.width = width;
      newShape.height = height;
      newShape.w = width;
      newShape.h = height;
      newShape.fontSize = this.params.fontSize || 16;
      newShape.fontFamily = this.params.fontFamily || 'Arial';
      newShape.fillColor = 'transparent';
      newShape.strokeColor = 'transparent';
      newShape.attrs = {
        category: 'text',
        isText: true
      };
    } else if (this.params.category === 'sticky-note' || this.params.type === 'sticky-note') {
      // Handle sticky notes
      newShape.type = 'sticky-note';
      newShape.text =
        this.params.text || (i18n.t('genogram:shapes.stickyNoteDefault', { defaultValue: 'Note' }) as string);
      // Get default dimensions from sticky-note manifest
      const stickyNoteManifest = getShapeManifest('genogram', 'sticky-note');
      const defaultDims = stickyNoteManifest?.defaultDimensions || { width: 150, height: 150 };
      const width = this.params.width || defaultDims.width;
      const height = this.params.height || defaultDims.height;
      // Set both width/height AND w/h for consistency with Electric projection
      newShape.width = width;
      newShape.height = height;
      newShape.w = width;
      newShape.h = height;
      newShape.fillColor = this.params.fillColor || '#FEF3C7';
      newShape.strokeColor = this.params.strokeColor || '#FCD34D';
      const v = VersionedComponentRegistry.getDefaultVersion('sticky-note', 'shape');
      (newShape as any).version = v;
      newShape.attrs = { ...(newShape.attrs || {}), __shapeVersion: v, category: 'sticky-note' };
    } else if (this.params.type === 'table') {
      newShape.type = 'table';
      const manifest =
        getShapeManifest('genogram', 'table') ||
        getShapeManifest('flowchart', 'table') ||
        getShapeManifest('org', 'table');
      const defaultDims = manifest?.defaultDimensions || { width: 240, height: 180 };
      const width = this.params.width || defaultDims.width;
      const height = this.params.height || defaultDims.height;
      newShape.width = width;
      newShape.height = height;
      newShape.w = width;
      newShape.h = height;
      if (typeof this.params.text === 'string') {
        newShape.text = this.params.text;
      }
      newShape.fillColor = this.params.fillColor || '#FFFFFF';
      newShape.strokeColor = this.params.strokeColor || '#97a3b6';
      const version = VersionedComponentRegistry.getDefaultVersion('table', 'shape');
      (newShape as any).version = version;
      const defaultProps =
        typeof manifest?.defaultProps === 'function'
          ? manifest.defaultProps()
          : (manifest?.defaultProps ?? {});
      newShape.attrs = {
        ...(newShape.attrs || {}),
        ...(defaultProps as Record<string, unknown>),
        ...(this.params.attrs ?? {}),
      };
    } else if (this.params.type === 'image' || this.params.type === 'pdf' || this.params.category === 'media') {
      const mediaShapeType = this.params.type === 'pdf' ? 'pdf' : 'image';
      newShape.type = mediaShapeType;
      const manifest = getShapeManifest('generic', mediaShapeType);
      const defaultDims = manifest?.defaultDimensions || { width: 240, height: 160 };
      const width = this.params.width || defaultDims.width;
      const height = this.params.height || defaultDims.height;
      newShape.width = width;
      newShape.height = height;
      newShape.w = width;
      newShape.h = height;
      const version = VersionedComponentRegistry.getDefaultVersion(mediaShapeType, 'shape');
      (newShape as any).version = version;
      const defaultProps =
        typeof manifest?.defaultProps === 'function'
          ? manifest.defaultProps()
          : (manifest?.defaultProps ?? {});
      newShape.attrs = {
        ...(newShape.attrs || {}),
        ...(defaultProps as Record<string, unknown>),
        ...(this.params.attrs ?? {}),
      };
    } else if (this.params.category === 'org') {
      // Handle org chart shapes
      newShape.category = 'orgchart';
      const orgShapeType =
        this.params.type === 'org_position' ||
        this.params.type === 'org_person' ||
        this.params.type === 'org_person_photo' ||
        this.params.type === 'org_person_photo_left' ||
        this.params.type === 'org_person_photo_right' ||
        this.params.type === 'org_person_photo_inner_left' ||
        this.params.type === 'org_person_text' ||
        this.params.type === 'org_department'
          ? this.params.type
          : 'org_position';
      const orgManifest = getShapeManifest('org', orgShapeType);
      const orgDefaultProps =
        typeof orgManifest?.defaultProps === 'function'
          ? orgManifest.defaultProps()
          : orgManifest?.defaultProps || {};
      const orgDefaultDims = orgManifest?.defaultDimensions || { width: 270, height: 80 };
      const orgWidth = this.params.width || orgDefaultDims.width;
      const orgHeight = this.params.height || orgDefaultDims.height;
      const orgVersion = VersionedComponentRegistry.getDefaultVersion(orgShapeType, 'shape');

      newShape.type = orgShapeType;
      newShape.width = orgWidth;
      newShape.height = orgHeight;
      newShape.w = orgWidth;
      newShape.h = orgHeight;
      (newShape as any).version = orgVersion;

      const defaultPersonName = getOrgChartDefaultPersonName();
      const defaultRole = getOrgChartDefaultRole();
      const defaultPositionTitle = getOrgChartDefaultPositionTitle();
      const defaultDepartmentName = getOrgChartDefaultDepartmentName();
      const paramAttrs = (this.params.attrs ?? {}) as Record<string, unknown>;

      newShape.attrs = {
        ...orgDefaultProps,
        __shapeVersion: orgVersion,
      };

      const isOrgPersonShape =
        orgShapeType === 'org_person' ||
        orgShapeType === 'org_person_photo' ||
        orgShapeType === 'org_person_photo_left' ||
        orgShapeType === 'org_person_photo_right' ||
        orgShapeType === 'org_person_photo_inner_left' ||
        orgShapeType === 'org_person_text';

      if (isOrgPersonShape) {
        newShape.attrs = {
          ...newShape.attrs,
          name: defaultPersonName,
          role: defaultRole,
        };
      } else if (orgShapeType === 'org_position') {
        newShape.attrs = {
          ...newShape.attrs,
          title: defaultPositionTitle,
        };
        newShape.text = defaultPositionTitle;
        newShape.name = '';
      } else if (orgShapeType === 'org_department') {
        newShape.attrs = {
          ...newShape.attrs,
          name: defaultDepartmentName,
        };
      }

      newShape.attrs = {
        ...newShape.attrs,
        ...paramAttrs,
        // Keep picker drops at authored world coords during collapse-reflow
        // until a primary reporting edge attaches the node to the tree.
        authoredPlacement: true,
      };

      if (orgShapeType === 'org_position') {
        newShape.text = String((newShape.attrs as { title?: string }).title ?? defaultPositionTitle);
        newShape.name = '';
      } else if (isOrgPersonShape) {
        newShape.name = String((newShape.attrs as { name?: string }).name ?? defaultPersonName);
      } else if (orgShapeType === 'org_department') {
        newShape.name = String((newShape.attrs as { name?: string }).name ?? defaultDepartmentName);
      }
    } else if (this.params.category === 'network') {
      // Network / Systems Mapping. `params.type` is the shape manifest id
      // (e.g. `'aws_ec2'`, `'basic_router'`). The manifest-first dispatch in
      // NodesRenderer finds the manifest via `tryManifestRender(node.type, ...)`,
      // which tries `node.type` first and then provider-prefixed variants.
      // We keep `node.type = manifestId` so lookup is deterministic and
      // works without needing to strip provider prefixes client-side.
      //
      // Crucially, ManifestSvgNode reads the canonical `resourceType` from
      // `manifest.defaultProps()` so color/icon lookup via the AWS icon
      // registry still works regardless of how `node.type` was stamped.
      const networkManifest = getShapeManifest('network' as any, this.params.type);
      const networkDefaultProps =
        typeof networkManifest?.defaultProps === 'function'
          ? networkManifest.defaultProps()
          : (networkManifest?.defaultProps ?? {});
      const networkDefaultDims = networkManifest?.defaultDimensions || { width: 160, height: 60 };
      const networkWidth = this.params.width || networkDefaultDims.width;
      const networkHeight = this.params.height || networkDefaultDims.height;
      const networkVersion = VersionedComponentRegistry.getDefaultVersion(this.params.type, 'shape');

      const defaults = networkDefaultProps as Record<string, unknown>;

      newShape.type = this.params.type;
      newShape.category = 'network';
      newShape.width = networkWidth;
      newShape.height = networkHeight;
      newShape.w = networkWidth;
      newShape.h = networkHeight;
      (newShape as any).version = networkVersion;

      // Preserve an explicitly supplied label; otherwise use the manifest's default name or a
      // humanised version of the shape id ("aws_ec2" → "Aws ec2").
      const defaultName =
        (defaults.name as string | undefined)
        || this.params.type.replace(/^(aws|azure|gcp|basic)_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const displayName = typeof this.params.text === 'string'
        ? this.params.text
        : typeof this.params.attrs?.name === 'string'
          ? this.params.attrs.name
          : defaultName;
      newShape.name = displayName;
      newShape.text = displayName;

      // Spread the manifest defaults across top-level fields AND attrs so:
      //   - ManifestSvgNode and any future generic renderer can read them,
      //   - profile-scoring adapters that flatten attrs into props see the
      //     data they expect (resourceType, semanticType, region, etc.),
      //   - the property panel's field-pack tools pick up the initial
      //     values so newly placed nodes don't look empty.
      for (const [key, value] of Object.entries(defaults)) {
        if (value === undefined) continue;
        // Don't clobber values we've already intentionally set above.
        if (['type', 'width', 'height', 'w', 'h', 'category', 'name', 'text', 'id'].includes(key)) continue;
        (newShape as any)[key] = value;
      }
      newShape.attrs = {
        ...(newShape.attrs || {}),
        ...defaults,
        ...(this.params.attrs ?? {}),
        name: displayName,
        __shapeVersion: networkVersion,
      };
      if (typeof defaults.fillColor === 'string') {
        newShape.fillColor = defaults.fillColor as string;
      }
      if (typeof defaults.strokeColor === 'string') {
        newShape.strokeColor = defaults.strokeColor as string;
      }
    } else {
      // Generic shapes for other diagram categories
      const providedAttrs =
        this.params.attrs && typeof this.params.attrs === 'object'
          ? (this.params.attrs as Record<string, unknown>)
          : {};
      const registeredShape = resolveRegisteredShapeCategoryManifest(
        this.params.category,
        this.params.type,
      );

      if (registeredShape) {
        const { manifest } = registeredShape;
        const defaultProps =
          typeof manifest.defaultProps === 'function'
            ? manifest.defaultProps()
            : {};
        const defaults = defaultProps as Record<string, unknown>;
        const defaultDims = manifest.defaultDimensions || { width: 120, height: 80 };
        const width = this.params.width || defaultDims.width;
        const height = this.params.height || defaultDims.height;
        const version = VersionedComponentRegistry.getDefaultVersion(this.params.type, 'shape');
        const resolvedText =
          typeof this.params.text === 'string'
            ? this.params.text
            : typeof providedAttrs.label === 'string'
              ? (providedAttrs.label as string)
              : typeof providedAttrs.text === 'string'
                ? (providedAttrs.text as string)
                : typeof defaults.name === 'string'
                  ? (defaults.name as string)
                  : this.params.type;

        newShape.type = manifest.id;
        newShape.category = manifest.category;
        newShape.name = resolvedText;
        newShape.text = resolvedText;
        newShape.width = width;
        newShape.height = height;
        newShape.w = width;
        newShape.h = height;
        (newShape as any).version = version;

        for (const [key, value] of Object.entries(defaults)) {
          if (value === undefined) continue;
          if (['type', 'width', 'height', 'w', 'h', 'category', 'name', 'text', 'id'].includes(key)) continue;
          (newShape as any)[key] = value;
        }

        newShape.attrs = {
          ...(newShape.attrs || {}),
          ...defaults,
          ...providedAttrs,
          variant: this.params.type,
          category: this.params.category,
          manifestCategory: manifest.category,
          label: resolvedText,
          text: resolvedText,
          fontSize: this.params.fontSize ?? (providedAttrs.fontSize as number | undefined),
          fontFamily: this.params.fontFamily ?? (providedAttrs.fontFamily as string | undefined),
          __shapeVersion: version,
        };

        if (typeof defaults.fillColor === 'string') {
          newShape.fillColor = defaults.fillColor as string;
        }
        if (typeof defaults.strokeColor === 'string') {
          newShape.strokeColor = defaults.strokeColor as string;
        }
      } else {
        const variant =
          typeof providedAttrs.variant === 'string' && providedAttrs.variant.length > 0
            ? providedAttrs.variant
            : this.params.type;
        const mapCategoryToType = (category?: string) => {
          switch (category) {
            case 'basic':
              return 'basic-shape';
            case 'flowchart':
              return 'flowchart-shape';
            case 'bpmn':
              return 'bpmn-shape';
            case 'orgchart':
              return 'org-shape';
            default:
              return 'basic-shape';
          }
        };

        const nodeType = mapCategoryToType(this.params.category);
        newShape.type = nodeType;
        newShape.name = variant;

        // Resolve default dimensions from manifests when available
        const manifestCategory = (() => {
          if (this.params.category === 'basic') return 'flowchart';
          if (this.params.category === 'flowchart') return 'flowchart';
          if (this.params.category === 'bpmn') return 'flowchart';
          if (this.params.category === 'orgchart') return 'org';
          return 'flowchart';
        })();
        const manifest = getShapeManifest(manifestCategory as any, this.params.type) ||
          (this.params.category === 'basic'
            ? getShapeManifest('genogram', this.params.type)
            : undefined);
        const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };
        const width = this.params.width || defaultDims.width;
        const height = this.params.height || defaultDims.height;
        newShape.width = width;
        newShape.height = height;
        newShape.w = width;
        newShape.h = height;

        newShape.fillColor = this.params.fillColor || '#FFFFFF';
        newShape.strokeColor = this.params.strokeColor || '#1E293B';

        const version = VersionedComponentRegistry.getDefaultVersion(nodeType);
        (newShape as any).version = version;

        const resolvedText =
          typeof this.params.text === 'string'
            ? this.params.text
            : typeof providedAttrs.label === 'string'
              ? (providedAttrs.label as string)
              : typeof providedAttrs.text === 'string'
                ? (providedAttrs.text as string)
                : undefined;

        newShape.attrs = {
          ...(newShape.attrs || {}),
          ...providedAttrs,
          variant,
          category: this.params.category,
          label: resolvedText,
          text: resolvedText,
          fontSize: this.params.fontSize ?? (providedAttrs.fontSize as number | undefined),
          fontFamily: this.params.fontFamily ?? (providedAttrs.fontFamily as string | undefined),
        };
      }
    }
    
    // Apply visual defaults before the initial Yjs write. A follow-up style
    // command cannot rely on the new node already reaching the store.
    const initialStyle = this.params.initialStyle ?? {};
    for (const field of ['fillColor', 'strokeColor', 'fontSize', 'fontFamily', 'strokeWidth']) {
      if (initialStyle[field] !== undefined) newShape[field] = structuredClone(initialStyle[field]);
    }
    for (const field of ['fontSize', 'fontFamily', 'strokeWidth', 'textColor', 'dash', 'dashEnabled', 'fontWeight', 'fontStyle', 'textDecoration', 'textAlign', 'verticalAlign']) {
      if (initialStyle[field] !== undefined) {
        newShape.attrs = { ...newShape.attrs, [field]: structuredClone(initialStyle[field]) };
      }
    }

    // Use MutationManager to write to Yjs (single source of truth)
    // MutationManager always has access to the current Yjs doc
    const mutationManager = getNodeMutationManager();
    // Save created node for reliable redo
    this.createdNode = newShape;
    try {
      console.debug('Debug SDI: CreateShapeCommand.execute addNode', {
        createdId: this.createdId,
        type: newShape?.type,
        x: newShape?.x,
        y: newShape?.y,
        w: newShape?.w,
        h: newShape?.h
      });
    } catch {}
    mutationManager.addNode(newShape);
    
    // Check if the shape was created on a table cell and auto-dock it
    // Use requestAnimationFrame to ensure the node is in the store and tables are rendered
    const createdId = this.createdId;
    if (!createdId) return;
    
    requestAnimationFrame(() => {
      try {
        const currentStore = useDiagramStore.getState();
        const createdNode = currentStore.nodes.find((n) => n.id === createdId);
        if (!createdNode) return;

        // Allow any shape type to be docked (except tables themselves)
        // This enables genogram shapes, basic shapes, and any other shape types
        const nodeType = (createdNode as any).type;
        if (!nodeType || nodeType === 'table') return;

        const shapeX = (createdNode.x ?? 0) as number;
        const shapeY = (createdNode.y ?? 0) as number;
        const shapeW = ((createdNode.width ?? createdNode.w) ?? 60) as number;
        const shapeH = ((createdNode.height ?? createdNode.h) ?? 60) as number;
        
        const centerX = shapeX + shapeW / 2;
        const centerY = shapeY + shapeH / 2;
        const originalCenterX = originalPos.x + shapeW / 2;
        const originalCenterY = originalPos.y + shapeH / 2;

        // Find all tables and check if the shape is in any cell
        // Try center first, then top-left corner as fallback
        const tables = currentStore.nodes.filter((n) => (n as any).type === 'table');
        for (const table of tables) {
          const pointsToTry: Array<{ pt: { x: number; y: number }; kind: 'center' | 'topLeft' }> = [
            { pt: { x: centerX, y: centerY }, kind: 'center' },
            { pt: { x: originalCenterX, y: originalCenterY }, kind: 'center' },
            { pt: { x: shapeX, y: shapeY }, kind: 'topLeft' },
            { pt: { x: originalPos.x, y: originalPos.y }, kind: 'topLeft' },
          ];

          let hit: any | null = null;
          let hitPoint: (typeof pointsToTry)[number] | null = null;
          for (const p of pointsToTry) {
            hit = getTableDropHitAtPoint(table, p.pt);
            if (hit) {
              hitPoint = p;
              break;
            }
          }
          if (hit) {
            // Shape was created on a table cell - dock it
            const attrs = (table.attrs ?? {}) as Record<string, unknown>;
            const layout = resolveCellLayoutConfig(attrs, hit.cellKey as any);
            const targetRect = hit.contentRect;
            
            // Calculate position based on layout algorithm
            const size = { w: shapeW, h: shapeH };
            const dragTopLeft =
              hitPoint?.kind === 'center'
                ? { x: hitPoint.pt.x - size.w / 2, y: hitPoint.pt.y - size.h / 2 }
                : hitPoint?.pt ?? { x: shapeX, y: shapeY };
            const fixedOffset = {
              x: Math.max(0, Math.min(dragTopLeft.x - targetRect.x, targetRect.w - size.w)),
              y: Math.max(0, Math.min(dragTopLeft.y - targetRect.y, targetRect.h - size.h)),
            };
            const snapped = {
              x: targetRect.x + targetRect.w / 2 - size.w / 2,
              y: targetRect.y + targetRect.h / 2 - size.h / 2,
            };
            const targetPos = layout.algorithmId === 'grid' ? snapped : { x: targetRect.x + fixedOffset.x, y: targetRect.y + fixedOffset.y };

            const dockTarget =
              layout.algorithmId === 'grid'
                ? { tableId: hit.tableId, cellKey: hit.cellKey }
                : { tableId: hit.tableId, cellKey: hit.cellKey, offset: fixedOffset };

            const cmdManager = getCommandManager();

            const dockCmd = new DockNodeToTableCellCommand(createdId, targetPos, { x: shapeX, y: shapeY }, {
              mode: 'dock',
              dock: dockTarget,
            });
            cmdManager.execute(dockCmd);
            
            // Always trigger layout reflow after docking (handles both fixed and grid layouts)
            // Use requestAnimationFrame to ensure the dock command's store updates have propagated
            requestAnimationFrame(() => {
              try {
                cmdManager.execute(new ReflowDockedNodesInTableCellCommand(hit.tableId, hit.cellKey));
                
                // Check for auto-resize after reflow
                const autoResizeCells = (attrs as any).autoResizeCells === true;
                if (autoResizeCells) {
                  requestAnimationFrame(() => {
                    try {
                      cmdManager.execute(new AutoResizeTableCellToFitDockedContentCommand(hit.tableId, hit.cellKey));
                    } catch (err) {
                      console.debug('Auto-resize on create failed:', err);
                    }
                  });
                }
              } catch (err) {
                console.debug('Reflow on create failed:', err);
              }
            });

            break; // Only dock to the first matching table
          }
        }
      } catch (error) {
        // Silently fail - this is a convenience feature
        try {
          console.debug('Auto-dock on create failed:', error);
        } catch {}
      }
    });
    
    if (this.createdId && this.createdNode) {
      const created = this.createdNode as any;
      const nodeType = created.type;
      if (nodeType === 'table') {
        const attrs = (created.attrs ?? {}) as Record<string, unknown>;
        const rowCount = typeof attrs.rowCount === 'number' && Number.isFinite(attrs.rowCount) ? attrs.rowCount : 3;
        const columnCount =
          typeof attrs.columnCount === 'number' && Number.isFinite(attrs.columnCount) ? attrs.columnCount : 3;
        const diagramId =
          typeof window !== 'undefined'
            ? ((window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? 'demo-diagram')
            : 'demo-diagram';
        const schemaId = typeof attrs.schemaId === 'string' ? attrs.schemaId : undefined;
        const creationMethod = schemaId ? 'schema' : 'toolbar';

        Tracker.track(TABLE.CREATED, {
          tableId: this.createdId,
          diagramId,
          initialRows: rowCount,
          initialCols: columnCount,
          creationMethod,
        });

        if (schemaId) {
          try {
            // Keep this registry lazy-loaded; table schemas are only needed for table creation analytics.
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { getTableSchema } = require('../../domains/diagram/shapes/table/tableSchemas.registry') as {
              getTableSchema: (id: string) => { id: string; name: string; category: string } | null;
            };
            const schema = getTableSchema(schemaId);
            if (schema) {
              Tracker.track(TABLE_ADVANCED.SCHEMA_CREATED, {
                tableId: this.createdId,
                diagramId,
                schemaId: schema.id,
                schemaName: schema.name,
                category: schema.category,
                initialRows: rowCount,
                initialCols: columnCount,
              });
            }
          } catch {
          }
        }
      }
    }

    // Select the new shape
    store.setSelected(new Set([this.createdId]));
    this.executed = true;
  }

  undo(): void {
    if (!this.createdId) return;
    
    // Use MutationManager to delete from Yjs (single source of truth)
    const mutationManager = getNodeMutationManager();
    try { console.debug('Debug SDI: CreateShapeCommand.undo deleteNode', { id: this.createdId }); } catch {}
    mutationManager.deleteNode(this.createdId);
    const removedKeys = collectMediaKeysFromNodes([this.createdNode]);
    emitDiagramMediaReferenceDelta({ removedKeys });
    
    console.log('[CreateShapeCommand] ✅ Removed shape via MutationManager:', this.createdId);
  }

  redo(): void {
    // Redo must re-add the exact same node (same id and props)
    if (!this.createdId || !this.createdNode) {
      // Fallback: if somehow missing, execute a fresh create (may produce new id)
      this.executed = false;
      return this.execute();
    }
    const mutationManager = getNodeMutationManager();
    try { console.debug('Debug SDI: CreateShapeCommand.redo addNode', { id: this.createdId }); } catch {}
    mutationManager.addNode(this.createdNode);
    const addedKeys = collectMediaKeysFromNodes([this.createdNode]);
    emitDiagramMediaReferenceDelta({ addedKeys });
    const store = useDiagramStore.getState();
    store.setSelected(new Set([this.createdId]));
  }

  getCreatedId(): string | undefined {
    return this.createdId;
  }

  syncCreatedNodeAttrsForRedo(nodeId: string, attrsPatch: Record<string, unknown>): void {
    if (!this.createdId || this.createdId !== nodeId || !this.createdNode) {
      return;
    }
    const node = this.createdNode as { attrs?: Record<string, unknown> };
    node.attrs = { ...(node.attrs ?? {}), ...attrsPatch };
  }
}
